import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import en from '../translations/en';
import fr from '../translations/fr';

const SUPPORTED_LANGUAGES = ['en', 'fr'];
const translations = { en, fr };

const detectBrowserLanguage = () => {
  const lang = String(typeof navigator !== 'undefined' ? (navigator.language || '') : '').toLowerCase();
  if (lang.startsWith('fr')) return 'fr';
  return 'en';
};

const normalizeLanguage = (value) => {
  const lang = String(value || '').trim().toLowerCase().slice(0, 2);
  return SUPPORTED_LANGUAGES.includes(lang) ? lang : 'en';
};

const LanguageContext = createContext(null);

export const LanguageProvider = ({ children }) => {
  const [language, setLanguageState] = useState(() => detectBrowserLanguage());
  const manuallySetRef = useRef(false);

  const setLanguage = useCallback((lang) => {
    const normalized = normalizeLanguage(lang);
    manuallySetRef.current = true;
    setLanguageState(normalized);
  }, []);

  const applyServerLanguage = useCallback((lang) => {
    if (!manuallySetRef.current && lang) {
      const normalized = normalizeLanguage(lang);
      setLanguageState(normalized);
    }
  }, []);

  const t = useCallback((key, params) => {
    const keys = key.split('.');
    let result = translations[language] || translations.en;
    for (const k of keys) {
      result = result?.[k];
      if (result === undefined) break;
    }
    let str = typeof result === 'string' ? result : key;
    if (params && typeof params === 'object') {
      for (const [k, v] of Object.entries(params)) {
        str = str.replace(`{${k}}`, String(v));
      }
    }
    return str;
  }, [language]);

  return (
    <LanguageContext.Provider value={{ language, setLanguage, applyServerLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguageContext = () => {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error('useLanguageContext must be used inside LanguageProvider');
  return ctx;
};
